import { Module, GetterTree, ActionTree, MutationTree } from 'vuex';
import http from '@/http';

const state: QuestionnaireAnswerState = {
  questionnaireAnswer: {},
  questionnaire: {},
  report: {},
  questionOffset: 0,
  questionGroupOffset: 0,
  showAutoSaveMsg: false,
  dismissAutoSaveMsg: false,
  onboarding: 0,
  questionGroupIndex: -1,
  healthCheckSummary: undefined,
};

const getters: GetterTree<QuestionnaireAnswerState, RootState> = {
  questionGroups: (state) => {
    return state.questionnaire.question_groups;
  },
};

const actions: ActionTree<QuestionnaireAnswerState, RootState> = {
  fetch({ commit }, oid) {
    return http.v1.qna
      .fetch({
        params: {
          booking_candidate: oid,
        },
      })
      .then((response) => {
        const payload = response.data.data;

        if (!payload) {
          return response;
        }

        commit('INIT_REPORT_DATA', {
          questionnaire: payload.questionnaire,
          questionnaire_answer: payload,
        });

        return response;
      });
  },
  fetchNextQuestion({ state, commit }, config) {
    return http.v1.qna
      .fetchQuestion(state.questionnaireAnswer.object_id, config)
      .then((response) => {
        commit(
          'SET_QUESTION_GROUP_OFFSET',
          response.data.data.question_group_offset
        );
        commit('SET_QUESTION_OFFSET', response.data.data.question_offset);
        return response;
      });
  },
  updateAnswer({ state, commit }, payload) {
    return http.v1.qna
      .updateAnswer(state.questionnaireAnswer.object_id, payload)
      .then((response) => {
        if (!state.dismissAutoSaveMsg) {
          commit('SET_SHOW_AUTO_SAVE_MSG', true);
        }
        commit(
          'SET_QUESTION_GROUP_OFFSET',
          response.data.data.question_group_offset
        );
        commit('SET_QUESTION_OFFSET', response.data.data.question_offset);
        return response;
      });
  },
  fetchPrevQuestion({ state, commit }, payload) {
    return http.v1.qna
      .fetchPrevQuestion(state.questionnaireAnswer.object_id, payload)
      .then((response) => {
        commit(
          'SET_QUESTION_GROUP_OFFSET',
          response.data.data.question_group_offset
        );
        commit('SET_QUESTION_OFFSET', response.data.data.question_offset);
        return response;
      });
  },
  complete({ state }) {
    return http.v1.qna.complete(state.questionnaireAnswer.object_id);
  },
  fetchResult({ commit }, oid) {
    return http.v1.qna.fetchResult(oid).then((response) => {
      const payload = response.data.data;

      if (!payload) {
        return response;
      }

      commit('INIT_REPORT_DATA', payload);

      return response;
    });
  },
  fetchResultForReview({ commit }, oid) {
    return http.v1.qna.fetchResultForReview(oid).then((response) => {
      const payload = response.data.data;

      if (!payload) {
        return response;
      }

      commit('INIT_REPORT_DATA', payload);

      return response;
    });
  },
  fetchResultForPreview({ commit }, oid) {
    return http.v1.qna.fetchResultForPreview(oid).then((response) => {
      const payload = response.data.data;

      if (!payload) {
        return response;
      }

      commit('INIT_REPORT_DATA', payload);

      return response;
    });
  },
  startOver(context, oid) {
    return http.v1.qna.startOver(oid);
  },
  confirmCandidateProfile({ commit }, oid) {
    return http.v1.qna
      .confirmCandidateProfile({
        params: {
          booking_candidate: oid,
        },
      })
      .then((response) => {
        const payload = response.data.data;

        if (!payload) {
          return response;
        }

        commit('SET_QUESTIONNAIRE_ANSWER_STATUS', payload.status);

        return response;
      });
  },
  submitGuardianInfo({ state, commit }, guardianInfo) {
    return http.v1.qna
      .submitGuardianInfo(state.questionnaireAnswer.object_id, {
        guardian_info: guardianInfo,
      })
      .then((response) => {
        const payload = response.data.data;

        if (!payload) {
          return response;
        }

        commit('SET_QUESTIONNAIRE_ANSWER_STATUS', payload.status);

        return response;
      });
  },
  downloadReport(content, questionnaireAnswerOid) {
    return http.v1.qna.downloadReport(questionnaireAnswerOid);
  },
  dismissAutoSaveMsg({ commit }) {
    commit('SET_SHOW_AUTO_SAVE_MSG', false);
    commit('SET_DISMISS_AUTO_SAVE_MSG', true);
  },
  attachReport(content, payload) {
    return http.v1.qna.attachReport(
      payload.questionnaireAnswerOid,
      payload.report
    );
  },
  downloadAttachedReport(content, questionnaireAnswerOid) {
    return http.v1.qna.downloadAttachedReport(questionnaireAnswerOid);
  },
  fetchHCS({ commit }, oid) {
    return http.v1.qna.fetchHCS(oid).then((response) => {
      const payload = response.data;

      if (!payload) {
        return response;
      }

      commit('SET_HEALTH_CHECK_SUMMARY', payload);

      return response;
    });
  },
  downloadHCSReport(content, questionnaireAnswerOid) {
    return http.v1.qna.downloadHCSReport(questionnaireAnswerOid);
  },
};

const mutations: MutationTree<QuestionnaireAnswerState> = {
  SET_QUESTIONNAIRE_ANSWER(state, payload) {
    state.questionnaireAnswer = payload;
  },
  INIT_REPORT_DATA(state, payload) {
    state.questionnaire = payload.questionnaire ?? state.questionnaire;
    state.questionnaireAnswer =
      payload.questionnaire_answer ?? state.questionnaireAnswer;
    state.report = payload;
  },
  SET_QUESTION_GROUP_OFFSET(state, payload) {
    state.questionGroupOffset = payload;
  },
  SET_QUESTION_OFFSET(state, payload) {
    state.questionOffset = payload === false ? -1 : payload;
  },
  SYNC_QUESTION_ANSWER(state, payload) {
    for (const index in state.questionnaireAnswer.question_answers) {
      const questionAnswer = state.questionnaireAnswer.question_answers[index];
      if (questionAnswer.object_id === payload.object_id) {
        state.questionnaireAnswer.question_answers[index] = payload;
      }
    }
  },
  UPDATE_PROGRESS(state, payload) {
    if (
      payload.last_question_group_object_id &&
      payload.last_question_group_progress
    ) {
      const lastQuestionGroupOffset = state.questionnaire.question_groups.findIndex(
        (qg) => qg.object_id === payload.last_question_group_object_id
      );
      if (lastQuestionGroupOffset > -1) {
        state.questionnaire.question_groups[lastQuestionGroupOffset].progress =
          payload.last_question_group_progress;
      } else {
        console.warn('Last Question Group Not Found');
      }
    }

    state.questionnaire.question_groups[
      payload.question_group_offset
    ].progress = payload.question_group_progress;
  },
  CLEAR_STATE(state) {
    state.questionnaireAnswer = {};
    state.questionnaire = {};
    state.report = {};
    state.showAutoSaveMsg = false;
    state.dismissAutoSaveMsg = false;
  },
  SET_QUESTIONNAIRE_ANSWER_STATUS(state, payload) {
    state.questionnaireAnswer.status = payload;
  },
  SET_SHOW_AUTO_SAVE_MSG(state, payload) {
    state.showAutoSaveMsg = payload;
  },
  SET_DISMISS_AUTO_SAVE_MSG(state, payload) {
    state.dismissAutoSaveMsg = payload;
  },
  SET_REPORT_PAGE(state, payload) {
    state.onboarding = payload;
  },
  NEXT_REPORT_PAGE(state) {
    console.debug('next');
    state.onboarding =
      state.onboarding + 1 === state.report.pages ? 0 : state.onboarding + 1;
  },
  PREV_REPORT_PAGE(state) {
    console.debug('prev');
    state.onboarding =
      state.onboarding - 1 < 0 ? state.report.pages : state.onboarding - 1;
  },
  SET_QUESTION_GROUP_INDEX(state, payload) {
    state.questionGroupIndex = payload;
  },
  SET_HEALTH_CHECK_SUMMARY(state, payload) {
    state.healthCheckSummary = payload;
  },
};

const questionnaireAnswer: Module<QuestionnaireAnswerState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

export default questionnaireAnswer;
